/* Loft – product card videos
   - 1st media is a video: autoplay, muted, loop (only while visible on screen)
   - 2nd media is a video: plays on hover (desktop / fine pointer only)
   - If autoplay is blocked (e.g. iOS Low Power Mode), the video stays hidden and the
     first-frame image rendered underneath remains as the cover. */
(() => {
  if (window.__loftCardVideoInit) return;
  window.__loftCardVideoInit = true;

  const bound = new WeakSet();

  const prep = (video) => {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };

  const tryPlay = (video) => {
    prep(video);
    const p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // Autoplay blocked (Low Power Mode / data saver): keep the static cover image
        video.classList.remove('is-playing');
        video.dataset.autoplayBlocked = 'true';
      });
    }
  };

  const observer = 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            if (video.dataset.autoplayBlocked !== 'true' && !video.dataset.hoverPaused) tryPlay(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { rootMargin: '200px 0px' })
    : null;

  const bindPrimary = (video) => {
    prep(video);
    video.addEventListener('playing', () => video.classList.add('is-playing'));
    if (observer) observer.observe(video); else tryPlay(video);
  };

  const bindSecondary = (video) => {
    prep(video);
    const figure = video.closest('.product-card__figure');
    if (!figure || bound.has(figure)) return;
    bound.add(figure);
    const primary = figure.querySelector('.product-card__video--primary');

    figure.addEventListener('mouseenter', () => {
      if (!window.matchMedia('(pointer: fine)').matches) return;
      if (primary && !primary.paused) { primary.dataset.hoverPaused = '1'; primary.pause(); }
      tryPlay(video);
    });
    figure.addEventListener('mouseleave', () => {
      video.pause();
      try { video.currentTime = 0; } catch (e) {}
      if (primary && primary.dataset.hoverPaused) {
        delete primary.dataset.hoverPaused;
        if (primary.dataset.autoplayBlocked !== 'true') tryPlay(primary);
      }
    });
  };

  const scan = (root) => {
    (root || document).querySelectorAll('video.product-card__video:not([data-loft-bound])').forEach((video) => {
      video.setAttribute('data-loft-bound', '');
      if (video.classList.contains('product-card__video--primary')) bindPrimary(video);
      else if (video.classList.contains('product-card__video--secondary')) bindSecondary(video);
    });
  };

  const start = () => {
    scan(document);
    // Product lists are replaced by AJAX (filters, sorting, pagination, quick views…)
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => { queued = false; scan(document); });
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/* Loft – header search open: on iPhone (Safari) the product gallery was painted on top of the open search panel.
   While the search is open, <html> gets the class "loft-search-open" and the page content is hidden behind the
   dark overlay (CSS in loft-card-video.css). Detection uses the panel's real visibility, not a specific attribute. */
(() => {
  if (window.__loftSearchOpenInit) return;
  window.__loftSearchOpenInit = true;

  const html = document.documentElement;
  let queued = false;

  const isOpen = () => Array.prototype.some.call(document.querySelectorAll('header-search, .header-search'), (el) => {
    if (el.hasAttribute('open')) return true;
    const cs = getComputedStyle(el);
    return cs.display !== 'none' && cs.visibility !== 'hidden';
  });

  const update = () => {
    queued = false;
    html.classList.toggle('loft-search-open', isOpen());
  };
  const queue = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(update);
  };

  const start = () => {
    new MutationObserver((records) => {
      for (const r of records) {
        const t = r.target;
        if (t.nodeType === 1 && (t.matches('header-search, .header-search') || t.closest('header-search, .header-search'))) { queue(); return; }
      }
    }).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['open', 'class', 'style', 'hidden', 'aria-hidden'] });

    ['click', 'keydown', 'focusin', 'touchend'].forEach((type) => document.addEventListener(type, () => setTimeout(queue, 50), true));
    update();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
